/* eslint-disable react/display-name */

import React, { useEffect, useMemo, useState } from 'react';
import useSWR from "swr";
import { config } from "../core";
import 'chartjs-plugin-colorschemes';
import { Doughnut, HorizontalBar, Bar, Line, Pie } from "react-chartjs-2";
import DashboardCard from "../components/cards/DashboardCard";
import IncomingInspectionsMap from "../components/IncomingInspectionsMap";
import IncomingMonthlyTrend from "../components/IncomingMonthlyTrend";
import { InspectorTable as LateDashInspectorTable, StateTable as LateDashStateTable } from "../components/late/LateDashTables";
import TabPanel from "../components/Tabs/TabPanel";
import { CircularProgress, Tabs, Tab } from "@material-ui/core";
import { Dialog, DialogContent } from "@material-ui/core";
import InspectionPreContact from "./InspectionPreContact";
import { withStyles } from "@material-ui/core/styles";
import { useSortBy, useTable } from "react-table";
import { apiReports } from "../api/reports";
import { DateTime } from "luxon";

// --- Shared constants ---
const categoryNames = ['Scheduled', 'Part Scheduled', 'Not Scheduled', 'New'];
const chartColors = ['#94ba33', '#F8ED00', '#d71433', '#00acdc'];
const cardColors = ['tw-bg-fs-green', 'tw-bg-yellow-400', 'tw-bg-fs-red', 'tw-bg-primary'];
const lateNames = ['Scheduled', 'Part Scheduled', 'Not Scheduled'];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Late Report colors
const red = '#d71433';
const yellow = '#F8ED00';
const amber = 'rgba(251,191,36)';
const green = '#94ba33';
const black = '#000000';

// --- Styled tabs ---
const StyledTabs = withStyles((theme) => ({
  indicator: {
    backgroundColor: theme.palette.primary.main,
  },
}))((props) => <Tabs {...props} />);

const StyledTab = withStyles((theme) => ({
  root: {
    color: '#000000',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(15),
    textTransform: "uppercase",
    letterSpacing: "-.01em",
    '&:focus': { opacity: 1 },
  },
}))((props) => <Tab disableRipple {...props} />);

// --- Chart options ---
const doughnutOptions = {
  legend: { display: false },
  maintainAspectRatio: false,
  cutoutPercentage: 55,
};

const lateBarOptions = {
  legend: { display: true, position: 'bottom' },
  maintainAspectRatio: false,
  scales: {
    xAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
    yAxes: [{ stacked: true }],
  },
  tooltips: { mode: 'index', intersect: false },
};

const livePieOptions = { legend: { display: false, position: 'left' } };
const MonthlyPieOptions = { legend: { display: true, position: 'left' } };

// --- Helpers ---
// sentiment: "bad" = up is red, "neutral" = always gray
const ChangeIndicator = ({ change, sentiment = "bad" }) => {
  if (change === 0) {return <span className="tw-text-gray-400 tw-text-xs">no change</span>;}
  const isUp = change > 0;
  let colorClass = "tw-text-gray-500";
  if (sentiment === "bad") {colorClass = isUp ? "tw-text-red-600" : "tw-text-green-600";}
  return (
    <span className={`tw-font-semibold ${colorClass}`}>
      <i className={`fa fa-arrow-${isUp ? 'up' : 'down'} tw-mr-1`} />
      {Math.abs(change)} {isUp ? "more" : "fewer"} than yesterday
    </span>
  );
};

const line = <p className={"tw-border-secondary tw-bg-secondary tw-py-1 tw-border-0.5 tw-rounded-lg tw-mb-4"}/>;

const GetComingDue = ({ ComingDue }) => ComingDue;
const GetLate = ({ Open, Late }) => {
  if (Open === 0) {return '0 (0%)';}
  return `${Late} (${(Late / Open * 100).toFixed(0)}%)`;
};

// --- Inspector Table (from Late Report) ---
const inspectorPieOptions = { maintainAspectRatio: false, legend: { display: false }, tooltips: { enabled: false } };

const InspectorTable = ({ data }) => {
  const columns = useMemo(() => [
    { Header: "Inspector", accessor: "Inspector" },
    { Header: "Assigned", accessor: "Assigned" },
    { Header: "Submitted", accessor: "Submitted" },
    { Header: "Turnover", accessor: "Turnover", Cell: props => `${props.row.original.Turnover.toFixed(1)}%` },
    { Header: "Late", accessor: "Late", Cell: props => `${props.row.original.Late} (${props.row.original.LatePercent.toFixed(1)}%)` },
    { Header: "", accessor: "chart", Cell: props => {
      const onTime = (Number(props.row.original.Submitted) || 0) - (Number(props.row.original.Late) || 0);
      const late = Number(props.row.original.Late) || 0;
      return <div className={"tw-w-20 tw-h-14"}><Pie data={{ datasets: [{ data: [onTime, late], backgroundColor: [green, red] }], labels: ["On Time", "Late"] }} options={inspectorPieOptions} /></div>;
    } },
  ], []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns, data, initialState: { sortBy: [{ id: "Inspector", desc: false }] },
  }, useSortBy);

  return (
    <table className="tw-mt-10 tw-min-w-full tw-divide-y tw-divide-gray-200" {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup, i) =>
          <tr key={`${i}`} className={"tw-bg-fs-dark-blue tw-bg-opacity-25 tw-rounded-t-lg"} {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) =>
              <th key={`${i}-${j}`} className={`tw-px-6 tw-py-3 tw-text-left tw-leading-4 tw-font-medium tw-uppercase tw-tracking-wider tw-font-bold ${column.isSorted ? column.isSortedDesc ? 'sort-desc' : 'sort-asc' : ''}`}
                {...column.getHeaderProps(column.getSortByToggleProps())}>
                {column.render("Header")} {column.isSorted ? column.isSortedDesc ? <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1"><i className="fa fa-sort-down"/></div> : <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1"><i className="fa fa-sort-up"/></div> : ''}
              </th>
            )}
          </tr>
        )}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr key={`${i}`} {...row.getRowProps()}>
              {row.cells.map((cell, j) => {
                let classnames = "tw-px-6 tw-py-2 ";
                i % 2 ? classnames = classnames + "tw-bg-gray-200" : null;
                return <td key={`${i}-${j}`} className={classnames} {...cell.getCellProps()}>{cell.render("Cell")}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// =============================================
// MAIN COMPONENT
// =============================================
const SupportDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [riskFilter, setRiskFilter] = useState(null);
  const [contactPolicyNo, setContactPolicyNo] = useState(null);

  // --- Scheduling tab data ---
  const { data: dashData, error } = useSWR(
    `${config.api.baseUrl}/reports/supportdashboard`,
    { refreshInterval: 30000 }
  );

  // --- Late Management tab data ---
  const { data: lateDashData } = useSWR(
    `${config.api.baseUrl}/reports/inspectionlates/dash`,
    { refreshInterval: 60000 }
  );

  // --- Sparkline metric history ---
  const metricKeys = [
    "sched_total_open", "sched_scheduled", "sched_part_scheduled", "sched_not_scheduled", "sched_new",
    "sched_scheduled_late", "sched_part_scheduled_late", "sched_not_scheduled_late",
    "live_all_open", "live_late_30", "live_coming_due_5", "live_submitted_30", "live_received_today", "live_received_30",
  ];

  const historyByMetric = {};
  metricKeys.forEach((key) => {
    const { data } = useSWR(`metric-history-${key}`, () => apiReports.getMetricHistory(key, 30));
    historyByMetric[key] = data?.data?.result || null;
  });

  const loading = !dashData && !error;
  const categories = dashData?.result?.Categories || [];
  const total = dashData?.result?.Total || 0;
  const totalChange = dashData?.result?.TotalChange;
  const hasYesterday = categories.some(c => c.YesterdayCount > 0);
  const lateCategories = categories.slice(0, 3);

  const doughnutData = categories.length > 0 ? {
    labels: categories.map(c => c.Name),
    datasets: [{ data: categories.map(c => c.Count), backgroundColor: chartColors, borderWidth: 2, borderColor: '#ffffff' }],
  } : null;

  // Turnaround data processing
  const allTurnaround = dashData?.result?.Turnaround || [];
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const monthlyTurnaround = allTurnaround.filter(t => new Date(t.Year, t.Month - 1, 1) >= cutoff);

  const yearlyMap = {};
  allTurnaround.forEach(t => {
    if (!yearlyMap[t.Year]) {yearlyMap[t.Year] = { volume: 0, avgSum: 0, medSum: 0, d0: 0, d8: 0, d15: 0, d22: 0, d31: 0, d45: 0 };}
    const y = yearlyMap[t.Year];
    y.volume += t.Volume; y.avgSum += t.Average * t.Volume; y.medSum += t.Median * t.Volume;
    y.d0 += t.Days0to7; y.d8 += t.Days8to14; y.d15 += t.Days15to21;
    y.d22 += t.Days22to30; y.d31 += t.Days31to45; y.d45 += t.Days45Plus;
  });
  const years = Object.keys(yearlyMap).sort();

  const distColors = {
    d0: { bg: 'rgba(34, 197, 94, 0.8)', border: '#22c55e' }, d8: { bg: 'rgba(132, 204, 22, 0.8)', border: '#84cc16' },
    d15: { bg: 'rgba(250, 204, 21, 0.8)', border: '#facc15' }, d22: { bg: 'rgba(251, 146, 60, 0.8)', border: '#fb923c' },
    d31: { bg: 'rgba(239, 68, 68, 0.8)', border: '#ef4444' }, d45: { bg: 'rgba(127, 29, 29, 0.8)', border: '#7f1d1d' },
  };

  const buildTrendData = (items, labels) => ({
    labels,
    datasets: [
      { type: 'bar', label: 'Inspection Volume', data: items.map(t => t.volume || t.Volume), backgroundColor: 'rgba(0, 172, 220, 0.7)', borderColor: '#00acdc', borderWidth: 1, yAxisID: 'y-volume', order: 2 },
      { type: 'line', label: 'Average Days', data: items.map(t => { const avg = t.avgSum ? t.avgSum / (t.volume || 1) : t.Average; return Math.round(avg * 10) / 10; }), borderColor: '#45456e', backgroundColor: '#45456e', borderWidth: 2, pointRadius: 3, fill: false, yAxisID: 'y-days', order: 1 },
      { type: 'line', label: 'Median Days', data: items.map(t => { const med = t.medSum ? t.medSum / (t.volume || 1) : t.Median; return Math.round(med * 10) / 10; }), borderColor: '#5fb600', backgroundColor: '#5fb600', borderWidth: 2, pointRadius: 3, borderDash: [5, 5], fill: false, yAxisID: 'y-days', order: 0 },
    ],
  });

  const trendOptions = {
    maintainAspectRatio: false,
    tooltips: { mode: 'index', intersect: false, callbacks: { label: function(item, data) { const label = data.datasets[item.datasetIndex].label; const val = item.yLabel; if (label.includes('Days')) {return `${label}: ${val.toFixed(1)} days`;} return `${label}: ${val.toLocaleString()}`; } } },
    scales: {
      yAxes: [
        { id: 'y-volume', type: 'linear', position: 'left', ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: 'Inspection Volume' } },
        { id: 'y-days', type: 'linear', position: 'right', ticks: { beginAtZero: true }, gridLines: { drawOnChartArea: false }, scaleLabel: { display: true, labelString: 'Turnaround Days' } },
      ],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 14 } }],
    },
    legend: { position: 'top' },
  };

  const buildDistData = (items, labels) => {
    const mapped = items.map(t => ({ d0: t.d0 ?? t.Days0to7 ?? 0, d8: t.d8 ?? t.Days8to14 ?? 0, d15: t.d15 ?? t.Days15to21 ?? 0, d22: t.d22 ?? t.Days22to30 ?? 0, d31: t.d31 ?? t.Days31to45 ?? 0, d45: t.d45 ?? t.Days45Plus ?? 0 }));
    const pct = (key) => mapped.map(t => { const tot = t.d0 + t.d8 + t.d15 + t.d22 + t.d31 + t.d45; return tot > 0 ? t[key] / tot * 100 : 0; });
    return {
      labels,
      datasets: [
        { label: '0-7 days', data: pct('d0'), backgroundColor: distColors.d0.bg, borderColor: distColors.d0.border, borderWidth: 1 },
        { label: '8-14 days', data: pct('d8'), backgroundColor: distColors.d8.bg, borderColor: distColors.d8.border, borderWidth: 1 },
        { label: '15-21 days', data: pct('d15'), backgroundColor: distColors.d15.bg, borderColor: distColors.d15.border, borderWidth: 1 },
        { label: '22-30 days', data: pct('d22'), backgroundColor: distColors.d22.bg, borderColor: distColors.d22.border, borderWidth: 1 },
        { label: '31-45 days', data: pct('d31'), backgroundColor: distColors.d31.bg, borderColor: distColors.d31.border, borderWidth: 1 },
        { label: '45+ days', data: pct('d45'), backgroundColor: distColors.d45.bg, borderColor: distColors.d45.border, borderWidth: 1 },
      ],
    };
  };

  const distOptions = {
    maintainAspectRatio: false, legend: { position: 'top' },
    tooltips: { mode: 'index', intersect: false, callbacks: { label: function(item, data) { return `${data.datasets[item.datasetIndex].label}: ${item.yLabel.toFixed(1)}%`; } } },
    scales: { xAxes: [{ stacked: true }], yAxes: [{ stacked: true, ticks: { max: 100, callback: v => v + '%' }, scaleLabel: { display: true, labelString: 'Percentage' } }] },
  };

  const monthlyTrendData = monthlyTurnaround.length > 0 ? buildTrendData(monthlyTurnaround, monthlyTurnaround.map(t => `${MONTH_NAMES[t.Month - 1]} ${t.Year}`)) : null;
  const monthlyDistData = monthlyTurnaround.length > 0 ? buildDistData(monthlyTurnaround, monthlyTurnaround.map(t => `${MONTH_NAMES[t.Month - 1]} ${t.Year}`)) : null;
  const yearlyTrendData = years.length > 0 ? buildTrendData(years.map(y => yearlyMap[y]), years) : null;
  const yearlyDistData = years.length > 0 ? buildDistData(years.map(y => yearlyMap[y]), years) : null;

  // Square footage data processing
  const allSqft = dashData?.result?.SqftYears || [];

  const sqftTrendData = allSqft.length > 0 ? {
    labels: allSqft.map(s => s.Year.toString()),
    datasets: [{
      label: 'Average Sq Ft',
      data: allSqft.map(s => s.Average),
      borderColor: 'rgba(0, 172, 220, 1)',
      backgroundColor: 'rgba(0, 172, 220, 0.1)',
      borderWidth: 2,
      fill: true,
      lineTension: 0.3,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#fff',
      pointBorderColor: 'rgba(0, 172, 220, 1)',
      pointBorderWidth: 2,
    }],
  } : null;

  const sqftTrendOptions = {
    maintainAspectRatio: false,
    title: { display: true, text: 'Average Square Footage by Year', fontSize: 16 },
    legend: { display: false },
    tooltips: { callbacks: { label: function(item) { const idx = item.index; const count = allSqft[idx]?.Count || 0; return [`Average: ${item.yLabel.toLocaleString()} sq ft`, `Inspections: ${count.toLocaleString()}`]; } } },
    scales: {
      yAxes: [{ ticks: { callback: v => v.toLocaleString() }, scaleLabel: { display: true, labelString: 'Average Square Footage' } }],
      xAxes: [{ scaleLabel: { display: true, labelString: 'Year' } }],
    },
  };

  const sqftBands = [
    { key: 'Under1000', label: '< 1,000' },
    { key: 'Range1kTo2k', label: '1,000 - 2,000' },
    { key: 'Range2kTo3k', label: '2,000 - 3,000' },
    { key: 'Range3kTo5k', label: '3,000 - 5,000' },
    { key: 'Range5kTo10k', label: '5,000 - 10,000' },
    { key: 'Range10kTo25k', label: '10,000 - 25,000' },
    { key: 'Over25000', label: '25,000+' },
  ];
  const sqftDistTotals = sqftBands.map(b => allSqft.reduce((sum, s) => sum + (s[b.key] || 0), 0));
  const sqftDistTotal = sqftDistTotals.reduce((a, b) => a + b, 0);

  const sqftDistColors = [
    'rgba(147, 197, 253, 0.8)', 'rgba(96, 165, 250, 0.8)', 'rgba(59, 130, 246, 0.8)',
    'rgba(37, 99, 235, 0.8)', 'rgba(29, 78, 216, 0.8)', 'rgba(30, 64, 175, 0.8)', 'rgba(88, 28, 135, 0.8)',
  ];

  const sqftDistData = sqftDistTotal > 0 ? {
    labels: sqftBands.map(b => b.label),
    datasets: [{
      label: 'Inspections',
      data: sqftDistTotals,
      backgroundColor: sqftDistColors,
      borderColor: sqftDistColors.map(c => c.replace('0.8', '1')),
      borderWidth: 1,
    }],
  } : null;

  const sqftDistOptions = {
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: { callbacks: { label: function(item) { const count = item.xLabel; const pct = sqftDistTotal > 0 ? (count / sqftDistTotal * 100).toFixed(1) : 0; return `${count.toLocaleString()} inspections (${pct}%)`; } } },
    scales: {
      xAxes: [{ ticks: { beginAtZero: true, callback: v => v.toLocaleString() }, scaleLabel: { display: true, labelString: 'Number of Inspections' } }],
      yAxes: [{ scaleLabel: { display: true, labelString: 'Square Footage' } }],
    },
  };

  // Quality of Property data processing
  const QUALITY_SCORE_KEYS = ['Score1', 'Score2', 'Score3', 'Score4', 'Score5', 'Score6', 'Score7', 'Score8', 'Score9', 'Score10'];
  const QUALITY_SCORE_COLORS = [
    { bg: 'rgba(220, 38, 38, 0.8)', border: '#dc2626' }, // 1 - red
    { bg: 'rgba(239, 68, 68, 0.8)', border: '#ef4444' }, // 2 - red
    { bg: 'rgba(249, 115, 22, 0.8)', border: '#f97316' }, // 3 - orange
    { bg: 'rgba(251, 146, 60, 0.8)', border: '#fb923c' }, // 4 - orange
    { bg: 'rgba(250, 204, 21, 0.8)', border: '#facc15' }, // 5 - yellow
    { bg: 'rgba(234, 179, 8, 0.8)', border: '#eab308' }, // 6 - yellow
    { bg: 'rgba(163, 230, 53, 0.8)', border: '#a3e635' }, // 7 - lime
    { bg: 'rgba(132, 204, 22, 0.8)', border: '#84cc16' }, // 8 - lime
    { bg: 'rgba(34, 197, 94, 0.8)', border: '#22c55e' }, // 9 - green
    { bg: 'rgba(22, 163, 74, 0.8)', border: '#16a34a' }, // 10 - green
  ];

  const allQuality = dashData?.result?.QualityMonths || [];
  const qualityFrom2018 = allQuality.filter(q => q.Year >= 2018);

  // Monthly: last 12 months, group by year-month
  const qualityCutoff = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const qualityMonthlyFiltered = qualityFrom2018.filter(q => new Date(q.Year, q.Month - 1, 1) >= qualityCutoff);
  const qualityByMonth = {};
  qualityMonthlyFiltered.forEach(q => {
    const key = `${q.Year}-${String(q.Month).padStart(2, '0')}`;
    if (!qualityByMonth[key]) { qualityByMonth[key] = { year: q.Year, month: q.Month }; QUALITY_SCORE_KEYS.forEach(k => qualityByMonth[key][k] = 0); }
    QUALITY_SCORE_KEYS.forEach(k => qualityByMonth[key][k] += q[k]);
  });
  const qualityMonthKeys = Object.keys(qualityByMonth).sort();
  const qualityMonthlyData = qualityMonthKeys.length > 0 ? {
    labels: qualityMonthKeys.map(k => `${MONTH_NAMES[qualityByMonth[k].month - 1]} ${qualityByMonth[k].year}`),
    datasets: QUALITY_SCORE_KEYS.map((key, i) => ({
      label: `${i + 1}`,
      data: qualityMonthKeys.map(k => { const m = qualityByMonth[k]; const total = QUALITY_SCORE_KEYS.reduce((sum, sk) => sum + m[sk], 0); return total > 0 ? m[key] / total * 100 : 0; }),
      backgroundColor: QUALITY_SCORE_COLORS[i].bg,
      borderColor: QUALITY_SCORE_COLORS[i].border,
      borderWidth: 1,
    })),
  } : null;

  // Yearly: aggregate by year
  const qualityByYear = {};
  qualityFrom2018.forEach(q => {
    if (!qualityByYear[q.Year]) { qualityByYear[q.Year] = {}; QUALITY_SCORE_KEYS.forEach(k => qualityByYear[q.Year][k] = 0); }
    QUALITY_SCORE_KEYS.forEach(k => qualityByYear[q.Year][k] += q[k]);
  });
  const qualityYears = Object.keys(qualityByYear).sort();
  const qualityYearlyData = qualityYears.length > 0 ? {
    labels: qualityYears,
    datasets: QUALITY_SCORE_KEYS.map((key, i) => ({
      label: `${i + 1}`,
      data: qualityYears.map(y => { const yr = qualityByYear[y]; const total = QUALITY_SCORE_KEYS.reduce((sum, sk) => sum + yr[sk], 0); return total > 0 ? yr[key] / total * 100 : 0; }),
      backgroundColor: QUALITY_SCORE_COLORS[i].bg,
      borderColor: QUALITY_SCORE_COLORS[i].border,
      borderWidth: 1,
    })),
  } : null;

  const qualityOptions = {
    maintainAspectRatio: false,
    legend: { position: 'top' },
    tooltips: { callbacks: { label: function(item, data) { return `Score ${data.datasets[item.datasetIndex].label}: ${item.yLabel.toFixed(1)}%`; } } },
    scales: {
      xAxes: [{ stacked: true }],
      yAxes: [{ stacked: true, ticks: { max: 100, callback: function(value) { return value + '%'; } }, scaleLabel: { display: true, labelString: 'Percentage' } }],
    },
  };

  const qualityMonthlyOptions = { ...qualityOptions, title: { display: true, text: 'Quality of Property by Month', fontSize: 16 } };
  const qualityYearlyOptions = { ...qualityOptions, title: { display: true, text: 'Quality of Property by Year', fontSize: 16 } };

  const lateVsOnTimeData = lateCategories.length > 0 ? {
    labels: lateCategories.map(c => c.Name),
    datasets: [
      { label: 'Late (> 30 days)', data: lateCategories.map(c => c.Late || 0), backgroundColor: '#d71433' },
      { label: 'On Time', data: lateCategories.map(c => (c.Count || 0) - (c.Late || 0)), backgroundColor: '#94ba33' },
    ],
  } : null;

  // --- Late Management tab data (only fetch when tab active) ---
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(`${DateTime.now().year}-${DateTime.now().month}`);

  const { data: liveData } = useSWR(activeTab === 1 ? `${config.api.baseUrl}/reports/inspectionlates/live` : null, { refreshInterval: 30000 });
  const { data: timeData } = useSWR(activeTab === 1 ? `${config.api.baseUrl}/reports/inspectionlates/period?month=${selectedMonth}` : null, { refreshInterval: 30000 });
  const { data: lastThreeMonths } = useSWR(activeTab === 1 ? `${config.api.baseUrl}/reports/inspectionlates/last3months` : null, { refreshInterval: 0 });

  // --- Penalty Risk tab data (only fetch when tab active) ---
  const { data: penaltyRiskData } = useSWR(activeTab === 2 ? `${config.api.baseUrl}/reports/penaltyrisk` : null, { refreshInterval: 30000 });
  const { data: penaltyRiskDigest, mutate: mutateDigest } = useSWR(
    activeTab === 2 ? `${config.api.baseUrl}/reports/penaltyrisk/digest` : null,
    { refreshInterval: penaltyRiskDigest?.result?.status === 'generating' ? 3000 : 0 }
  );

  useEffect(() => { if (timeData) {setDataLoading(false);} }, [timeData]);

  const monthSelections = useMemo(() => {
    let res = [];
    const currentMonth = DateTime.now().startOf('month');
    for (let i = 0; i < 13; i++) {
      const thisMonth = currentMonth.minus({ months: i });
      res.push({ id: `${thisMonth.year}-${thisMonth.month}`, display: `${thisMonth.monthShort} ${thisMonth.year}` });
    }
    return res;
  }, []);

  function changeMonth(value) { setDataLoading(true); setSelectedMonth(value); }

  const liveDataCalc = useMemo(() => {
    if (!liveData) {return null;}
    const late = Number(liveData.result.Late) || 0;
    const due = Number(liveData.result.ComingDue) || 0;
    const ok = liveData.result.Open - due - late;
    const total = late + due + ok;
    return { ok, due, late, okPercent: ok / total * 100, duePercent: due / total * 100, latePercent: late / total * 100 };
  }, [liveData]);

  const policyStatus = dashData?.result?.PolicyStatus || { New: 0, Renewal: 0 };
  const policyStatusTotal = policyStatus.New + policyStatus.Renewal;
  const policyStatusBar = 
    <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-justify-center">
      <div className="tw-flex tw-h-6 tw-rounded-lg tw-overflow-hidden">
        <div className="tw-bg-fs-green" style={{ width: `${policyStatusTotal > 0 ? policyStatus.New / policyStatusTotal * 100 : 0}%` }} title={`New: ${policyStatus.New}`} />
        <div className="tw-bg-primary" style={{ width: `${policyStatusTotal > 0 ? policyStatus.Renewal / policyStatusTotal * 100 : 0}%` }} title={`Renewal: ${policyStatus.Renewal}`} />
      </div>
      <div className="tw-flex tw-justify-between tw-text-base tw-mt-3">
        <span>New {policyStatus.New} ({policyStatusTotal > 0 ? (policyStatus.New / policyStatusTotal * 100).toFixed(0) : 0}%)</span>
        <span>Renewal {policyStatus.Renewal} ({policyStatusTotal > 0 ? (policyStatus.Renewal / policyStatusTotal * 100).toFixed(0) : 0}%)</span>
      </div>
    </div>
  ;

  const liveDataCategoryCalc = useMemo(() => {
    if (!liveData) {return null;}
    const late30 = liveData.result.LateCategories.Late1 || 0;
    const late45 = liveData.result.LateCategories.Late2 || 0;
    const late180 = liveData.result.LateCategories.Late3 || 0;
    const excluded = liveData.result.Excluded || 0;
    const total = late30 + late45 + late180 + excluded;
    return { late30, late45, late180, excluded, late30Percent: late30 / total * 100, late45Percent: late45 / total * 100, late180Percent: late180 / total * 100, excludedPercent: excluded / total * 100 };
  }, [liveDataCalc]);

  const livePieData = useMemo(() => {
    if (!liveDataCalc) {return null;}
    return { datasets: [{ data: [liveDataCalc.ok, liveDataCalc.due, liveDataCalc.late], backgroundColor: [green, amber, red] }], labels: ["OK", "Coming Due", "Late"] };
  }, [liveDataCalc]);

  const lateCategoryPieData = useMemo(() => {
    if (!liveDataCategoryCalc) {return null;}
    return { datasets: [{ data: [liveDataCategoryCalc.late30, liveDataCategoryCalc.late45, liveDataCategoryCalc.late180, liveDataCategoryCalc.excluded], backgroundColor: [yellow, amber, red, black] }], labels: ["Late > 30 days", "Late > 45 days", "Late > 180 days", "Excluded Lates"] };
  }, [liveDataCategoryCalc]);

  const timePieData = useMemo(() => {
    if (!timeData) {return null;}
    const onTime = (Number(timeData.result.Summary.Submitted) || 0) - (Number(timeData.result.Summary.Late) || 0);
    const late = Number(timeData.result.Summary.Late) || 0;
    return { datasets: [{ data: [onTime, late], backgroundColor: [green, red] }], labels: ["On time", "Late"] };
  }, [timeData]);

  const get3MonthSummaryData = ({ Created, Submitted }) => 
    <div className={"tw-grid tw-grid-cols-3 tw-gap-2"}>
      <p className={"tw-text-sm"}>Received</p><p className={"tw-text-sm"}>Submitted</p><p className={"tw-text-sm"}>Turnover</p>
      <p className={"tw-text-xl"}>{Created}</p><p className={"tw-text-xl"}>{Submitted}</p><p className={"tw-text-xl"}>{(Submitted / Created * 100).toFixed(0)}%</p>
    </div>
  ;

  // =============================================
  // RENDER
  // =============================================
  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
        <h1>360 Management Overview</h1>
        {loading && <CircularProgress size={24} />}
      </div>

      <div className="tw-bg-white tw-mb-6">
        <StyledTabs value={activeTab} onChange={(e, v) => setActiveTab(v)} indicatorColor="primary">
          <StyledTab label="Scheduling" />
          <StyledTab label="Late Management" />
          <StyledTab label="Penalty Risk" />
        </StyledTabs>
      </div>

      {/* ==================== SCHEDULING TAB ==================== */}
      <TabPanel value={activeTab} index={0} componentName="mgmt">
        <div className="tw-mb-2 tw-text-lg">
          <span className="tw-font-semibold">{total}</span> open inspections
          {hasYesterday && <span className="tw-ml-3 tw-text-sm"><ChangeIndicator change={totalChange} sentiment="neutral" /></span>}
        </div>

        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6">
          {categoryNames.map((name, i) => {
            const cat = categories.find(c => c.Name === name);
            const sentiment = name === 'Not Scheduled' ? 'bad' : 'neutral';
            const typeMap = { 'Scheduled': 'scheduled', 'Part Scheduled': 'partsched', 'Not Scheduled': 'notsched', 'New': 'new' };
            const metricMap = { 'Scheduled': 'sched_scheduled', 'Part Scheduled': 'sched_part_scheduled', 'Not Scheduled': 'sched_not_scheduled', 'New': 'sched_new' };
            return <DashboardCard key={name} title={name} value={cat?.Count} secondValue={cat && cat.YesterdayCount > 0 ? <ChangeIndicator change={cat.Change} sentiment={sentiment} /> : null} color={cardColors[i]} onClick={() => window.open(`/reports/late/list/${typeMap[name]}`, "_blank")} sparklineData={historyByMetric[metricMap[name]]} />;
          })}
          <DashboardCard title="New vs Renewal" value={policyStatusBar} color="tw-bg-fs-dark-blue" fullHeightValue />
        </div>

        {lateCategories.length > 0 &&
          <>
            <h4 className="tw-text-lg tw-font-semibold tw-mt-8 tw-mb-4">Late Inspections ({'>'} 30 days)</h4>
            <div className="tw-grid tw-grid-cols-3 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-4">
              {lateNames.map((name, i) => {
                const cat = lateCategories.find(c => c.Name === name);
                const late = cat?.Late || 0;
                const count = cat?.Count || 0;
                const pct = count > 0 ? Math.round(late / count * 100) : 0;
                const lateTypeMap = { 'Scheduled': 'scheduled-late', 'Part Scheduled': 'partsched-late', 'Not Scheduled': 'notsched-late' };
                const metricMap = { 'Scheduled': 'sched_scheduled_late', 'Part Scheduled': 'sched_part_scheduled_late', 'Not Scheduled': 'sched_not_scheduled_late' };
                return <DashboardCard key={name} title={name} value={late} subtitle={`of ${count}`} secondValue={count > 0 ? `${pct}% are late` : null} color={cardColors[i]} onClick={() => window.open(`/reports/late/list/${lateTypeMap[name]}`, "_blank")} sparklineData={historyByMetric[metricMap[name]]} />;
              })}
            </div>
          </>
        }

        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-my-8">
          {doughnutData &&
            <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-6">
              <h4 className="tw-text-lg tw-font-semibold tw-mb-4">Open Inspection Breakdown</h4>
              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <div className="tw-flex tw-flex-col tw-justify-center tw-gap-3">
                  {categories.map((cat, i) => {
                    const pct = total > 0 ? (cat.Count / total * 100).toFixed(0) : 0;
                    return (
                      <div key={cat.Name} className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-w-8 tw-h-3 tw-inline-block tw-rounded-sm" style={{ backgroundColor: chartColors[i] }} />
                        <p className="tw-text-sm">{cat.Name} - {cat.Count} ({pct}%)</p>
                      </div>
                    );
                  })}
                </div>
                <div><Doughnut data={doughnutData} options={doughnutOptions} /></div>
              </div>
            </div>
          }
          {lateVsOnTimeData &&
            <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-6">
              <h4 className="tw-text-lg tw-font-semibold tw-mb-4">Late vs On Time</h4>
              <div style={{ height: '220px' }}><HorizontalBar data={lateVsOnTimeData} options={lateBarOptions} /></div>
            </div>
          }
        </div>

        <IncomingMonthlyTrend />

        <IncomingInspectionsMap />

        {(monthlyTrendData || yearlyTrendData) &&
          <>
            <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Turnaround Time</h4>
            <hr className="tw-border-primary tw-mb-6" />
            <h5 className="tw-text-lg tw-font-semibold tw-mb-4">Monthly Trends</h5>
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {monthlyTrendData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={monthlyTrendData} options={trendOptions} /></div></div>}
              {monthlyDistData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={monthlyDistData} options={distOptions} /></div></div>}
            </div>
            <h5 className="tw-text-lg tw-font-semibold tw-mb-4">Yearly Trends {years.length > 0 && <span className="tw-font-normal tw-text-gray-500">· {years[0]} – {years[years.length - 1]}</span>}</h5>
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {yearlyTrendData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={yearlyTrendData} options={trendOptions} /></div></div>}
              {yearlyDistData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={yearlyDistData} options={distOptions} /></div></div>}
            </div>
          </>
        }

        {(sqftTrendData || sqftDistData) &&
          <>
            <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Square Footage</h4>
            <hr className="tw-border-primary tw-mb-6" />
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {sqftDistData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><HorizontalBar data={sqftDistData} options={sqftDistOptions} /></div></div>}
              {sqftTrendData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Line data={sqftTrendData} options={sqftTrendOptions} /></div></div>}
            </div>
          </>
        }

        {(qualityMonthlyData || qualityYearlyData) &&
          <>
            <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Quality of Property</h4>
            <hr className="tw-border-primary tw-mb-6" />
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {qualityMonthlyData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={qualityMonthlyData} options={qualityMonthlyOptions} /></div></div>}
              {qualityYearlyData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={qualityYearlyData} options={qualityYearlyOptions} /></div></div>}
            </div>
          </>
        }
      </TabPanel>

      {/* ==================== LATE MANAGEMENT TAB ==================== */}
      <TabPanel value={activeTab} index={1} componentName="mgmt">
        {!config.externalAdmin &&
          <>
            <h4>Submission Turnover</h4>
            <div className={"tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 xl:tw-grid-cols-5 tw-gap-8 tw-my-6"}>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[0].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[0])}/>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[1].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[1])}/>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[2].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[2])}/>
              <DashboardCard title={"Total"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[3])} color={"tw-bg-gray-400"}/>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[4].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[4])} color={"tw-bg-green-600"}/>
            </div>
            {line}
          </>
        }

        <h4>Live</h4>
        <div className={"tw-mt-6 lg:tw-grid tw-grid-cols-2 tw-gap-8 tw-justify-start"}>
          <div className={"tw-mb-10 tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4"}>
            <div className={"tw-grid tw-grid-cols-2 tw-h-full"}>
              <div className={"tw-flex tw-flex-col tw-justify-center tw-gap-2"}>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-fs-green tw-w-8 tw-h-3 tw-inline-block"}/><p>{`OK - ${liveDataCalc ? liveDataCalc.ok : ""} (${liveDataCalc ? liveDataCalc.okPercent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-yellow-400 tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Coming Due - ${liveDataCalc ? liveDataCalc.due : ""} (${liveDataCalc ? liveDataCalc.duePercent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-fs-red tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Late - ${liveDataCalc ? liveDataCalc.late : ""} (${liveDataCalc ? liveDataCalc.latePercent.toFixed(0) : ""}%)`}</p></div>
              </div>
              <div>{livePieData && <Pie data={livePieData} options={livePieOptions}/>}</div>
            </div>
          </div>

          <div className={"tw-mb-10 tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4"}>
            <div className={"tw-grid tw-grid-cols-2 tw-h-full"}>
              <div className={"tw-flex tw-flex-col tw-justify-center tw-gap-2"}>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-w-8 tw-h-3 tw-inline-block"} style={{ backgroundColor: '#F8ED00' }}/><p>{`Late > 30 days - ${liveDataCategoryCalc ? liveDataCategoryCalc.late30 : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.late30Percent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-yellow-400 tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Late > 45 days - ${liveDataCategoryCalc ? liveDataCategoryCalc.late45 : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.late45Percent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-fs-red tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Late > 180 days - ${liveDataCategoryCalc ? liveDataCategoryCalc.late180 : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.late180Percent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-black tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Excluded - ${liveDataCategoryCalc ? liveDataCategoryCalc.excluded : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.excludedPercent.toFixed(0) : ""}%)`}</p></div>
              </div>
              <div>{lateCategoryPieData && <Pie data={lateCategoryPieData} options={livePieOptions}/>}</div>
            </div>
          </div>
        </div>

        <div className={"tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6"}>
          <DashboardCard title={"All Open"} value={liveData?.result?.Open} secondValue={liveData?.result?.Open > 0 ? `avg ${Math.round(liveData?.result?.OpenContacts / liveData?.result?.Open)} contacts` : null} onClick={() => window.open("/reports/late/list/allopen", "_blank")} sparklineData={historyByMetric['live_all_open']} />
          <DashboardCard title={"Late > 30 days"} value={liveData && liveData.result && GetLate(liveData.result)} secondValue={liveData?.result?.Late > 0 ? `avg ${Math.round(liveData?.result?.LateContacts / liveData?.result?.Late)} contacts` : null} onClick={() => window.open("/reports/late/list/late", "_blank")} sparklineData={historyByMetric['live_late_30']} />
          <DashboardCard title={"Coming Due (in 5 days)"} value={liveData && liveData.result && GetComingDue(liveData.result)} secondValue={liveData?.result?.ComingDue > 0 ? `avg ${Math.round(liveData?.result?.ComingDueContacts / liveData?.result?.ComingDue)} contacts` : null} onClick={() => window.open("/reports/late/list/comingdue", "_blank")} sparklineData={historyByMetric['live_coming_due_5']} />
          <DashboardCard title={"Submitted last 30 days"} value={liveData && liveData.result && liveData.result.SubmittedLastThirtyDays} secondValue={liveData && liveData.result && `Last 5 days: ${liveData.result.SubmittedLastFiveDays}`} sparklineData={historyByMetric['live_submitted_30']} />
        </div>

        <div className={"tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6"}>
          <DashboardCard title={"New (today)"} value={liveData?.result?.ReceivedToday} secondValue={liveData?.result?.ReceivedLastThirtyDays > 0 ? `Last 30 days: ${liveData.result.ReceivedLastThirtyDays}` : null} sparklineData={historyByMetric['live_received_today']} />
        </div>

        {!config.externalAdmin &&
          <>
            {line}
            <h4 className="tw-mt-8">Monthly</h4>
            <div className="tw-mt-4 tw-flex tw-gap-4 tw-text-lg tw-items-center tw-h-14">
              <p>Reporting period</p>
              <select className="tw-rounded-lg" value={selectedMonth} onChange={(e) => changeMonth(e.target.value)}>
                {monthSelections.map(monthSelection => <option key={monthSelection.id} value={monthSelection.id}>{monthSelection.display}</option>)}
              </select>
              {dataLoading && <CircularProgress color="primary"/>}
            </div>
            <div className="tw-mt-6 tw-w-1/2 tw-mx-auto tw-mb-10 tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4">
              {timePieData && <Pie data={timePieData} options={MonthlyPieOptions} height={60}/>}
            </div>
            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6">
              <DashboardCard title="Received" value={timeData?.result?.Summary?.Received}/>
              <DashboardCard title="Submitted" value={timeData?.result?.Summary?.Submitted}/>
              <DashboardCard title="Late" value={timeData?.result?.Summary && `${timeData.result.Summary.Late} (${timeData.result.Summary.LatePercent.toFixed(0)}%)`}/>
              <DashboardCard title="Turnover" value={timeData?.result?.Summary && `${timeData.result.Summary.Turnover.toFixed(0)}%`}/>
            </div>
          </>
        }

        {lateDashData?.result &&
          <>
            <hr className="tw-border-primary tw-my-8 tw-mb-16" />
            <h4 className="tw-text-xl tw-font-bold tw-mb-4">Late Performance</h4>
            <div className="lg:tw-grid tw-grid-cols-2 tw-gap-8 tw-mb-16">
              <div>
                <h5 className="tw-text-lg tw-font-semibold tw-mb-3">By State</h5>
                <LateDashStateTable data={lateDashData.result.States} />
              </div>
              <div>
                <h5 className="tw-text-lg tw-font-semibold tw-mb-3">By Inspector</h5>
                <LateDashInspectorTable data={lateDashData.result.Inspectors} />
              </div>
            </div>
          </>
        }
      </TabPanel>

      {/* ==================== PENALTY RISK TAB ==================== */}
      <TabPanel value={activeTab} index={2} componentName="mgmt">
        {/* AI Daily Digest */}
        {penaltyRiskDigest?.result?.status === 'ready' && 
          <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-4 tw-mb-6 tw-border tw-border-fs-dark-blue tw-border-opacity-20">
            <div className="tw-flex tw-items-start tw-gap-3">
              <span className="tw-text-lg">⚠️</span>
              <div>
                <p className="tw-text-sm tw-font-semibold tw-text-fs-dark-blue tw-mb-1">Daily Risk Summary <button className="tw-text-xs tw-text-primary hover:tw-underline tw-ml-2" onClick={async () => { await fetch(`${config.api.baseUrl}/reports/penaltyrisk/digest?refresh=true`, { headers: { Authorization: `Bearer ${config.api.jwt}` } }); mutateDigest(); }}>Regenerate</button></p>
                <p className="tw-text-sm tw-text-gray-700">{penaltyRiskDigest.result.summary}</p>
                <p className="tw-text-xs tw-text-gray-400 tw-mt-2">
                  Generated {new Date(penaltyRiskDigest.result.generatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        }
        {penaltyRiskDigest?.result?.status === 'generating' && 
          <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-4 tw-mb-6">
            <div className="tw-flex tw-items-center tw-gap-3">
              <CircularProgress size={20} />
              <span className="tw-text-sm tw-text-gray-600">Generating daily risk summary...</span>
            </div>
          </div>
        }

        {/* Summary cards row */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6">
          <DashboardCard title="🔴 Critical (≤3 days)" value={penaltyRiskData?.result?.summary?.critical || 0} color="tw-bg-fs-red" onClick={() => setRiskFilter(riskFilter === 'critical' ? null : 'critical')} />
          <DashboardCard title="🟠 High (≤8 days, no appt)" value={penaltyRiskData?.result?.summary?.high || 0} color="tw-bg-fs-red-light" onClick={() => setRiskFilter(riskFilter === 'high' ? null : 'high')} />
          <DashboardCard title="🟡 Watch (15+ days, no appt)" value={penaltyRiskData?.result?.summary?.watch || 0} color="tw-bg-yellow-400" onClick={() => setRiskFilter(riskFilter === 'watch' ? null : 'watch')} />
          <DashboardCard title="⚠️ Stalled (no contact 6+ days)" value={penaltyRiskData?.result?.summary?.stalled || 0} color="tw-bg-fs-dark-blue-light" onClick={() => setRiskFilter(riskFilter === 'stalled' ? null : 'stalled')} />
          <DashboardCard title="📋 Closeout Pending (6+ attempts)" value={penaltyRiskData?.result?.summary?.closeoutPending || 0} color="tw-bg-fs-red-dark" onClick={() => setRiskFilter(riskFilter === 'closeout-pending' ? null : 'closeout-pending')} />
        </div>

        {/* Risk table */}
        {riskFilter && 
          <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm tw-text-gray-600">Filtered: <strong>{riskFilter.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</strong></span>
            <button className="tw-text-sm tw-text-primary hover:tw-underline" onClick={() => setRiskFilter(null)}>Clear filter</button>
          </div>
        }
        {(() => {
          const allItems = penaltyRiskData?.result?.items || [];
          const filteredItems = riskFilter ? allItems.filter(item => item.riskLevel === riskFilter) : allItems;
          return filteredItems.length > 0 ? 
            <div className="tw-overflow-x-auto">
              <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                <thead>
                  <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Risk</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Policy #</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Type</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Status</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Policy Type</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Location</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Age</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Days Left</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Contacts</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Last Contact</th>
                    <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Appt</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item, i) => {
                    const riskColors = {
                      'critical': 'tw-bg-fs-red tw-text-white',
                      'high': 'tw-bg-fs-red-light tw-text-white',
                      'watch': 'tw-bg-yellow-400',
                      'stalled': 'tw-bg-fs-dark-blue-light tw-text-white',
                      'closeout-pending': 'tw-bg-fs-red-dark tw-text-white',
                    };
                    const riskLabels = {
                      'critical': '🔴 Critical',
                      'high': '🟠 High',
                      'watch': '🟡 Watch',
                      'stalled': '⚠️ Stalled',
                      'closeout-pending': '📋 Closeout',
                    };
                    return (
                      <tr key={item.policyNo} className={i % 2 ? 'tw-bg-gray-200' : ''}>
                        <td className="tw-px-6 tw-py-2">
                          <span className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold ${riskColors[item.riskLevel] || ''}`}>
                            {riskLabels[item.riskLevel] || item.riskLevel}
                          </span>
                        </td>
                        <td className="tw-px-6 tw-py-2">
                          <a href={`/inspections/${item.policyNo}/process/pre_r`} target="_blank" rel="noopener noreferrer" className="tw-text-primary hover:tw-underline">
                            {item.policyNo}
                          </a>
                          {new Date(item.createdAt) < new Date('2026-07-01') && 
                          <span className="tw-ml-2 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-xs tw-bg-gray-300 tw-text-gray-700">Old Contract</span>
                          }
                        </td>
                        <td className="tw-px-6 tw-py-2">{item.inspectionType || '—'}</td>
                        <td className="tw-px-6 tw-py-2">{item.prettyStatus || item.status}</td>
                        <td className="tw-px-6 tw-py-2">{item.policyStatus}</td>
                        <td className="tw-px-6 tw-py-2">{item.city ? `${item.city}, ${item.state}` : item.state || '—'}</td>
                        <td className="tw-px-6 tw-py-2">{item.ageDays}d</td>
                        <td className="tw-px-6 tw-py-2">
                          <span className={item.daysRemaining <= 3 ? 'tw-text-fs-red tw-font-bold' : item.daysRemaining <= 8 ? 'tw-text-fs-red-light tw-font-semibold' : ''}>
                            {item.daysRemaining}
                          </span>
                        </td>
                        <td className="tw-px-6 tw-py-2">
                          {item.contactAttempts > 0 ? 
                            <a href="#" onClick={(e) => { e.preventDefault(); setContactPolicyNo(item.policyNo); }} className="tw-text-primary hover:tw-underline">
                              {item.contactAttempts}
                            </a>
                            : '0'}
                        </td>
                        <td className="tw-px-6 tw-py-2">
                          {item.lastContactDate ? new Date(item.lastContactDate).toLocaleDateString() : '—'}
                        </td>
                        <td className="tw-px-6 tw-py-2">
                          {item.appointmentDate && new Date(item.appointmentDate).setHours(0,0,0,0) >= new Date().setHours(0,0,0,0) ? new Date(item.appointmentDate).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            : 
            <div className="tw-text-center tw-py-12 tw-text-gray-500">
              {penaltyRiskData ? `✅ No${riskFilter ? ' ' + riskFilter.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''} inspections at risk` : 'Loading...'}
            </div>
          ;
        })()}
      </TabPanel>

      {/* Contact dialog for penalty risk tab */}
      <Dialog open={!!contactPolicyNo} onClose={() => setContactPolicyNo(null)} maxWidth='xl' fullWidth={true}>
        <DialogContent>
          {contactPolicyNo && <InspectionPreContact policyNo={contactPolicyNo} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportDashboard;
